// Account security building blocks, used on the Account page and on the first-sign-in set-up page.
import { useEffect, useMemo, useState, type FormEvent } from 'react'
import qrcode from 'qrcode-generator'
import type { SessionInfo } from '@briary/shared'
import { api, ApiError } from '../../lib/api'
import { useAuth } from '../../lib/auth'
import { Badge, Button, Card, Notice, TextField, timeAgo } from '../../ui'

const message = (e: unknown) => (e instanceof ApiError ? e.message : 'Something went wrong. Please try again.')

export function ChangePassword({ onDone, intro }: { onDone?: () => void; intro?: string }) {
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [ok, setOk] = useState(false)
  const [busy, setBusy] = useState(false)

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    setOk(false)
    if (next !== confirm) return setError('The two new passwords don’t match.')
    setBusy(true)
    try {
      await api.post('/api/auth/password', { current, next })
      setOk(true)
      setCurrent('')
      setNext('')
      setConfirm('')
      onDone?.()
    } catch (err) {
      setError(message(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <form onSubmit={submit} noValidate>
      {intro && <p className="muted">{intro}</p>}
      {error && <Notice tone="bad">{error}</Notice>}
      {ok && <Notice tone="good">Password changed. You’ve been signed out on your other devices.</Notice>}
      <TextField label="Current password" type="password" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} />
      <TextField
        label="New password"
        type="password"
        autoComplete="new-password"
        hint="At least 10 characters. Three random words work well."
        value={next}
        onChange={(e) => setNext(e.target.value)}
      />
      <TextField label="New password again" type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} />
      <Button type="submit" variant="primary" busy={busy}>
        Change password
      </Button>
    </form>
  )
}

function QrCode({ text }: { text: string }) {
  const cells = useMemo(() => {
    const qr = qrcode(0, 'M')
    qr.addData(text)
    qr.make()
    const n = qr.getModuleCount()
    const rects: string[] = []
    for (let r = 0; r < n; r++) for (let c = 0; c < n; c++) if (qr.isDark(r, c)) rects.push(`M${c + 2} ${r + 2}h1v1h-1z`)
    return { n: n + 4, d: rects.join('') }
  }, [text])
  return (
    <svg viewBox={`0 0 ${cells.n} ${cells.n}`} width={184} height={184} role="img" aria-label="QR code for your authenticator app" style={{ background: '#fff', borderRadius: 8 }}>
      <path d={cells.d} fill="#1f2b26" />
    </svg>
  )
}

export function RecoveryCodes({ codes }: { codes: string[] }) {
  return (
    <>
      <Notice tone="warn">
        <strong>Save these recovery codes now.</strong> Each one works once if you lose your phone. They won’t be shown again.
      </Notice>
      <div className="codes">{codes.map((c) => <span key={c}>{c}</span>)}</div>
      <div className="row" style={{ marginTop: 12 }}>
        <Button size="sm" icon="file" onClick={() => void navigator.clipboard?.writeText(codes.join('\n'))}>
          Copy codes
        </Button>
      </div>
    </>
  )
}

export function TwoStep({ onDone }: { onDone?: () => void }) {
  const { me, refresh } = useAuth()
  const [setup, setSetup] = useState<{ secret: string; otpauthUrl: string } | null>(null)
  const [code, setCode] = useState('')
  const [codes, setCodes] = useState<string[] | null>(null)
  const [password, setPassword] = useState('')
  const [mode, setMode] = useState<'idle' | 'disable' | 'regen'>('idle')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  if (!me) return null

  const run = async (fn: () => Promise<void>) => {
    setError(null)
    setBusy(true)
    try {
      await fn()
    } catch (err) {
      setError(message(err))
    } finally {
      setBusy(false)
    }
  }

  if (codes) {
    return (
      <div>
        <RecoveryCodes codes={codes} />
        <div style={{ marginTop: 16 }}>
          <Button variant="primary" onClick={() => { setCodes(null); onDone?.() }}>
            I’ve saved them
          </Button>
        </div>
      </div>
    )
  }

  if (me.mfa.totp) {
    return (
      <div>
        {error && <Notice tone="bad">{error}</Notice>}
        <div className="row" style={{ marginBottom: 12 }}>
          <Badge tone="good">On</Badge>
          <span className="muted">Authenticator app · {me.mfa.recoveryCodesLeft} recovery codes left</span>
        </div>
        {mode === 'idle' ? (
          <div className="row">
            <Button size="sm" onClick={() => setMode('regen')}>New recovery codes</Button>
            {!me.mfa.required && <Button size="sm" variant="danger" onClick={() => setMode('disable')}>Turn off</Button>}
          </div>
        ) : (
          <form
            onSubmit={(e) => {
              e.preventDefault()
              void run(async () => {
                if (mode === 'disable') {
                  await api.post('/api/auth/totp/disable', { password })
                  await refresh()
                } else {
                  const r = await api.post<{ recoveryCodes: string[] }>('/api/auth/recovery-codes', { password })
                  setCodes(r.recoveryCodes)
                  await refresh()
                }
                setMode('idle')
                setPassword('')
              })
            }}
          >
            <TextField label="Confirm with your password" type="password" autoComplete="current-password" autoFocus value={password} onChange={(e) => setPassword(e.target.value)} />
            <div className="row">
              <Button type="submit" variant={mode === 'disable' ? 'danger' : 'primary'} busy={busy}>
                {mode === 'disable' ? 'Turn off two-step verification' : 'Make new codes'}
              </Button>
              <Button variant="ghost" onClick={() => setMode('idle')}>Cancel</Button>
            </div>
          </form>
        )}
      </div>
    )
  }

  if (!setup) {
    return (
      <div>
        {error && <Notice tone="bad">{error}</Notice>}
        <p className="muted">
          Adds a 6-digit code from an app on your phone (Microsoft Authenticator, Google Authenticator, 1Password…) when you sign in, so a stolen password isn’t enough.
        </p>
        <Button variant="primary" icon="shield" busy={busy} onClick={() => void run(async () => setSetup(await api.post('/api/auth/totp/setup')))}>
          Set up two-step verification
        </Button>
      </div>
    )
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        void run(async () => {
          const r = await api.post<{ recoveryCodes: string[] }>('/api/auth/totp/enable', { code })
          await refresh()
          setSetup(null)
          setCodes(r.recoveryCodes)
        })
      }}
    >
      {error && <Notice tone="bad">{error}</Notice>}
      <ol className="muted" style={{ paddingLeft: 18, marginTop: 0 }}>
        <li>Open your authenticator app and add an account.</li>
        <li>Scan this code (or type the key below).</li>
        <li>Enter the 6-digit code it shows.</li>
      </ol>
      <div className="row" style={{ alignItems: 'flex-start', gap: 20, marginBottom: 16 }}>
        <QrCode text={setup.otpauthUrl} />
        <div style={{ minWidth: 0, flex: 1 }}>
          <div className="caps" style={{ marginBottom: 4 }}>Key</div>
          <code style={{ wordBreak: 'break-all', fontSize: 13 }}>{setup.secret.replace(/(.{4})/g, '$1 ').trim()}</code>
        </div>
      </div>
      <TextField label="Code from the app" className="input-code" inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} />
      <Button type="submit" variant="primary" busy={busy} disabled={code.length !== 6}>
        Turn on
      </Button>
    </form>
  )
}

const deviceName = (ua: string | null) => {
  if (!ua) return 'Unknown device'
  const browser = /Edg\//.test(ua) ? 'Edge' : /Chrome\//.test(ua) ? 'Chrome' : /Firefox\//.test(ua) ? 'Firefox' : /Safari\//.test(ua) ? 'Safari' : 'Browser'
  const os = /Windows/.test(ua) ? 'Windows' : /iPhone|iPad/.test(ua) ? 'iPhone/iPad' : /Android/.test(ua) ? 'Android' : /Mac OS/.test(ua) ? 'Mac' : /Linux/.test(ua) ? 'Linux' : ''
  return os ? `${browser} on ${os}` : browser
}

export function Sessions() {
  const [rows, setRows] = useState<SessionInfo[] | null>(null)
  const [busy, setBusy] = useState(false)
  const load = () => api.get<SessionInfo[]>('/api/auth/sessions').then(setRows).catch(() => setRows([]))
  useEffect(() => { void load() }, [])

  return (
    <Card
      title="Where you’re signed in"
      actions={
        rows && rows.length > 1 ? (
          <Button size="sm" busy={busy} onClick={async () => { setBusy(true); await api.del('/api/auth/sessions/others'); await load(); setBusy(false) }}>
            Sign out everywhere else
          </Button>
        ) : null
      }
      pad={false}
    >
      <div className="table-wrap">
        <table className="table">
          <tbody>
            {(rows ?? []).map((s) => (
              <tr key={s.id}>
                <td>
                  <strong>{deviceName(s.userAgent)}</strong>
                  {s.current && <> <Badge tone="brand">This device</Badge></>}
                  <div className="muted" style={{ fontSize: 12 }}>{s.ip ?? ''}</div>
                </td>
                <td className="muted">Active {timeAgo(s.lastSeenAt)}</td>
                <td style={{ textAlign: 'right' }}>
                  {!s.current && (
                    <Button size="sm" variant="ghost" onClick={async () => { await api.del(`/api/auth/sessions/${s.id}`); await load() }}>
                      Sign out
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  )
}
